import { Modal, ModalBody, ModalDescription, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { MultiSelector } from "@/ds/custom/MultiSelector";
import { NumericInput } from "@/ds/custom/NumericInput";
import { Selector } from "@/ds/custom/Selector";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Field, FieldGroup } from "@/ds/shadcn/components/ui/field";
import { useListAllProductAttributes } from "@/features/common/api/useListAllProductAttributes";
import type { Filter } from "@/lib/domain/FilterConverters";
import { areFilterFieldValuesKnown, filterFieldMaxValue, filterFieldMinValue, FilterFieldOptions, getFilterFieldDefaultOperands, isFilterFieldNumeric, type FilterField } from "@/lib/domain/FilterField";
import { FilterOperatorOptions, isFilterOperatorNumeric, NonNumericFilterOperatorOptions, type FilterOperator } from "@/lib/domain/FilterOperator";
import { SearchOptionsFoilingOptions } from "@/lib/domain/SearchOptionsFoiling";
import { SearchOptionsRarityOptions } from "@/lib/domain/SearchOptionsRarity";
import { SearchOptionsSetOptions } from "@/lib/domain/SearchOptionsSet";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

export interface ModalFilterProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (filter: Filter) => void;
	forbiddenFields: FilterField[];
}

export const ModalFilter: FC<ModalFilterProps> = props => {
	const {
		open,
		onOpenChange,
		forbiddenFields
	} = props;

	const [filterField, setFilterField] = useState<FilterField | null>(null);
	const [filterOperator, setFilterOperator] = useState<FilterOperator | null>(null);
	const [filterValues, setFilterValues] = useState<string[]>([]);

	const filterOperatorOptions = useMemo(() => !filterField || isFilterFieldNumeric(filterField) ? FilterOperatorOptions : NonNumericFilterOperatorOptions, [filterField]);

	useEffect(() => {
		if (!filterField || !filterOperator || !isFilterOperatorNumeric(filterOperator) || isFilterFieldNumeric(filterField)) {
			return;
		}

		setFilterOperator(null);
	}, [filterField, filterOperator]);

	useEffect(() => {
		if (!filterField) {
			return;
		}

		setFilterValues(getFilterFieldDefaultOperands(filterField));
	}, [filterField]);

	const canConfirm = useMemo(() => {
		return !!filterField && !!filterOperator && filterValues.length > 0;
	}, [filterField, filterOperator, filterValues]);

	const onConfirmEnriched = useCallback(() => {
		if (!filterField || !filterOperator || filterValues.length === 0) {
			return;
		}

		props.onConfirm({
			field: filterField,
			operator: filterOperator,
			operands: filterValues
		});

		setFilterField(null);
		setFilterOperator(null);
		setFilterValues([]);
		onOpenChange(false);
	}, [filterField, filterOperator, filterValues, props.onConfirm, onOpenChange]);

	const filterFieldOptions = useMemo(() => {
		return FilterFieldOptions.filter(x => !forbiddenFields.includes(x.value));
	}, [forbiddenFields]);

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<ModalHeader>
				<ModalTitle>Add Filter</ModalTitle>
				<ModalDescription>
					Apply a filter to the current view.
				</ModalDescription>
			</ModalHeader>
			<ModalBody>
				<FieldGroup className="gap-3">
					<Field>
						<Selector
							value={filterField}
							onChange={setFilterField}
							options={filterFieldOptions}
							disabled={filterFieldOptions.length === 0}
							placeholder="Field" />
					</Field>
					<Field>
						<Selector
							value={filterOperator}
							onChange={setFilterOperator}
							options={filterOperatorOptions}
							placeholder="Operator" />
					</Field>
					<Field>
						{
							filterField ? (
								<FilterOperandInput
									filterField={filterField}
									values={filterValues}
									onChange={setFilterValues} />
							) : (
								<Selector
									value={""}
									onChange={() => undefined}
									options={[]}
									placeholder="Values"
									disabled={true} />
							)
						}
					</Field>
				</FieldGroup>
				<div className="flex justify-end items-center gap-3 pt-6">
					<Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
					<Button disabled={!canConfirm} onClick={onConfirmEnriched}>Confirm</Button>
				</div>
			</ModalBody>
		</Modal>
	);
};

interface FilterOperandInputProps {
	filterField: FilterField;
	values: string[];
	onChange: (values: string[]) => void;
}

const FilterOperandInput: FC<FilterOperandInputProps> = props => {
	const {
		filterField
	} = props;

	if (isFilterFieldNumeric(filterField)) {
		return (
			<FilterOperandInputNumeric {...props} />
		);
	}

	if (areFilterFieldValuesKnown(filterField)) {
		return (
			<FilterOperandInputValuesKnownSelector {...props} />
		);
	}

	return (
		<FilterOperandInputValuesUnknownSelector {...props} />
	);
};

const FilterOperandInputNumeric: FC<FilterOperandInputProps> = props => {
	const {
		filterField,
		values,
		onChange
	} = props;

	const convertedValue = useMemo(() => parseFloat(values[0] || "0"), [values]);
	const onChangeConverted = (value: number) => onChange([value.toString()]);

	return (
		<NumericInput
			value={convertedValue}
			onChange={onChangeConverted}
			maxValue={filterFieldMaxValue(filterField)}
			minValue={filterFieldMinValue(filterField)}
			maxDecimalPlaces={0} />
	);
};

const FilterOperandInputValuesKnownSelector: FC<FilterOperandInputProps> = props => {
	const {
		filterField,
		values,
		onChange
	} = props;

	const options = useMemo(() => {
		return filterField === "Set"
			? SearchOptionsSetOptions
			: filterField === "Rarity"
				? SearchOptionsRarityOptions
				: SearchOptionsFoilingOptions;
	}, [filterField]);

	// TODO: Combo-box.
	return (
		<MultiSelector
			values={values}
			onChange={onChange}
			options={options}
			placeholder="Values" />
	);
};

const FilterOperandInputValuesUnknownSelector: FC<FilterOperandInputProps> = props => {
	const {
		filterField,
		values,
		onChange
	} = props;

	const allProductAttributes = useListAllProductAttributes();
	const options = useMemo(() => {
		if (!allProductAttributes.data) {
			return [];
		}

		const list = allProductAttributes.data.attributes[filterField];
		if (!list) {
			return [];
		}

		return list.map(x => ({ label: x.description, value: x.value }));
	}, [allProductAttributes.data, filterField]);

	return (
		<MultiSelector
			values={values}
			onChange={onChange}
			options={options}
			placeholder="Values" />
	);
};
